import type { Content } from '@tiptap/core';
import EditorService from '../../EditorService';
import Fuse from 'fuse.js';
import { Store } from 'vuex';
import { translate } from '@/utilities';
import PhantomSuggesterKey from '../../../plugins/phantomSuggester/pluginKey';
import PopUpEditLink from '../../../popups/PopUpEditLink.vue';
import AiPopup from '../../../popups/AiPopup.vue';
import { VueRenderer } from '@tiptap/vue-3';
import tippy from 'tippy.js';
import { ResourceType } from '@/types/resources';
import { isInTable } from '@tiptap/pm/tables';
import { nanoid } from 'nanoid';
import { CalloutTypes } from '@superthread-com/extension-callouts/src';

type PhantomSuggesterCommand = (commandProps: Content | string) => void;

/**
 * TODO: The resource types should be more consistent. This
 * is only a patch because some older components described
 * comments like this.
 */
const ResourceVariants = {
  Comment: [ResourceType.Comment, 'Card comment', 'Page comment'] as unknown as ResourceType[],
};

export interface SlashCommand {
  run: (editor: EditorService, command: PhantomSuggesterCommand, store: Store<any>) => void;
  searchName: string;
  displayName?: string;
  icon: string;
  category: string;
  /**
   * Specifies which kinds of editors support
   * this command. (i.e. comment, page).
   * This is an allowlist.
   */
  resource: ResourceType[];
  /**
   * An optional filtering function. Despite the resources
   * being the same, sometimes another filtering criterion
   * is required.
   *
   * *Example:* quick cards are still card
   * resources, but they must not include the quick card command.
   * @param store The application store
   * @returns `false` when the option should be absent from the list
   */
  filter?: (store: Store<any>) => boolean;
}

type SearchResultCategory<T> = {
  title: string;
  results: T[];
  previousIndex: number;
  type: string;
};

/**
 * Some comands will not interact with the editor directly.
 * Hence, stopping the mention should be done manually.
 * This both removes the mention text and closes the search
 * menu.
 * @param editor The editor instance.
 */
function stopSuggester(editor: EditorService) {
  const { tr } = editor.state;
  tr.setMeta(PhantomSuggesterKey.command, {
    type: 'remove',
  });
  editor.view.dispatch(tr);
}

/**
 * Some commands should not be displayed in tables. This is a helper
 * function that's to be used within the *filter* method of each command.
 * Since tables are not a separate resource, they are not filterable by
 * that criterion.
 * @param store The application store
 * @returns
 */
function selectionIsInTable(store: Store<any>): boolean {
  const currentEditor = store.state.editors.editors?.[store.state.editors.currentEditorId];
  if (!currentEditor) return true;
  if (isInTable(currentEditor.view.state)) {
    return true;
  }
  return false;
}

export function showPopup(props: any) {
  const rendererComponent = new VueRenderer(PopUpEditLink as any, {
    // @ts-ignore
    parent: (window as any).StVueApp,
    props: { ...props, popup: {}, component: {} },
    editor: props.editor,
  });
  const rects = document.body.getBoundingClientRect();
  // use tippy to attach component on body element
  const popup = tippy('body', {
    getReferenceClientRect: () => ({
      ...rects,
    }),
    appendTo: () => document.body,
    content: rendererComponent.element || undefined,
    showOnCreate: true,
    interactive: true,
    placement: 'top-start',
    trigger: 'manual',
    maxWidth: 'none',
    animation: 'none',
    role: 'poper',
  });
  // update props with popup and component instance
  rendererComponent.updateProps({ ...props, popup, component: rendererComponent.component });
}

export const generateAndShowPopup = (props: any, highlightSelectionId: any) => {
  const { selection } = props.editor.state;
  const { from, to } = selection;
  // change selection just to hide bubble menu if it is visible
  // shouldShowBubbleMenu is called on selection change
  props.editor.commands.setTextSelection({
    from,
  });
  // return old selection
  props.editor.commands.setTextSelection({
    from,
    to,
  });
  const component = new VueRenderer(AiPopup as any, {
    props: {
      ...props,
      popup: {},
      component: {},
      highlightSelectionId,
    },
    editor: props.editor,
  });
  const highlightedNodes = document.querySelectorAll(
    `[data-highlighted="${highlightSelectionId}"]`
  );
  if (!highlightedNodes.length) return;

  const lastElement = highlightedNodes[highlightedNodes.length - 1];
  const rects = lastElement.getBoundingClientRect();

  // use tippy to attach component on body element
  const popup = tippy('body', {
    getReferenceClientRect: () => rects,
    appendTo: () => (props?.appendTo === 'body' ? document.body : props.editor.view.dom.parentNode),
    content: component.ref.$el,
    showOnCreate: true,
    interactive: true,
    placement: 'bottom',
    trigger: 'manual',
    maxWidth: 'none',
    animation: 'none',
    role: 'poper',
    onShow: (instance) => {
      instance.popper.classList.add('w-full', 'outline-none', 'ai-popup-tippy');
      requestAnimationFrame(() => {
        props.editor.commands.setTextSelection({ from, to });
      });
    },
  });

  // update props with popup and component instance
  component.updateProps({ ...props, popup: popup[0], component });
};

export function showAiPopup(props: any) {
  const highlightSelectionId = nanoid(6);
  const { selection } = props.editor.state;
  props.editor.commands.setPreCommentHighlight({
    highlight: highlightSelectionId,
    preCommentRange: selection,
  });

  requestAnimationFrame(() => {
    generateAndShowPopup(props, highlightSelectionId);
  });
}
const mediaPageFilter = (store: Store<any>) => {
  if (store.getters.currentEditor?.resourceId === ResourceType.PageTemplate) return false;
  return !store.getters.getQuickPage;
};

export const mediaPageRun = (
  editor: EditorService,
  command: PhantomSuggesterCommand,
  store: Store<any>
) => {
  store.dispatch('setPageUploadPopup', {
    id: 'page-insert-image',
    title: translate('quickPage.title.attachImageFrom'),
    editorId: editor?.id,
  });
  stopSuggester(editor);
};

const mediaCardFilter = (store: Store<any>) => {
  const resourceId = store.getters.currentEditor?.resourceId;
  if (resourceId === ResourceType.CardTemplate || resourceId === ResourceType.QuickChildCard)
    return false;
  return (
    !store.getters.getQuickPage || (store.getters.getQuickPage && store.getters.getQuickCardIsOn)
  );
};

export const mediaCardRun = (
  editor: EditorService,
  command: PhantomSuggesterCommand,
  store: Store<any>
) => {
  store.dispatch('setCardDescriptionAttachmentUploadPopup', {
    id: 'card-description-image',
    title: translate('quickPage.title.attachImageFrom'),
    editorId: editor?.id,
    resourceId: store.getters.currentEditor?.resourceId,
  });
  stopSuggester(editor);
};

export const mediaCommentRun = (
  editor: EditorService,
  command: PhantomSuggesterCommand,
  store: Store<any>
) => {
  if (editor.parentResourceType === ResourceType.Card) {
    store.dispatch('setCardCommentAttachmentUploadPopup', {
      id: 'card-new-comment',
      editorId: editor.id,
      resourceId: store.getters.currentEditor?.parentResourceId,
    });
  }
  if (editor.parentResourceType === ResourceType.Page) {
    store.dispatch('setPageUploadPopup', {
      id: 'comment-insert-image',
      title: translate('quickPage.title.attachImageFrom'),
      editorId: editor.id,
    });
  }
  stopSuggester(editor);
};

const baseMediaPage = {
  category: 'Media',
  resource: [ResourceType.Page, ResourceType.Note],
  keywords: translate('slash.keyword.media'),
  filter: mediaPageFilter,
  run: mediaPageRun,
};

const baseMediaCard = {
  category: 'Media',
  resource: [ResourceType.Card],
  keywords: translate('slash.keyword.media'),
  filter: mediaCardFilter,
  run: mediaCardRun,
};

const baseMediaComment = {
  category: 'Media',
  resource: [...ResourceVariants.Comment],
  keywords: translate('slash.keyword.media'),
  run: mediaCommentRun,
};

export const mediaCommands: SlashCommand[] = [
  {
    ...baseMediaComment,
    searchName: translate('slash.search.attach'),
    displayName: translate('slash.display.attach'),
    icon: 'paperclip',
  },
  // Attach command
  {
    ...baseMediaPage,
    searchName: translate('slash.search.attach'),
    displayName: translate('slash.display.attach'),
    icon: 'paperclip',
  },
  /**
   * This is the same command as above, but with a different resource type.
   */
  {
    ...baseMediaCard,
    searchName: translate('slash.search.attach'),
    displayName: translate('slash.display.attach'),
    icon: 'paperclip',
  },
  // Image command
  {
    ...baseMediaPage,
    searchName: translate('slash.search.image'),
    displayName: translate('slash.display.image'),
    icon: 'image-st',
  },
  /**
   * This is the same command as above, but with a different resource type.
   */
  {
    ...baseMediaCard,
    searchName: translate('slash.search.image'),
    displayName: translate('slash.display.image'),
    icon: 'image-st',
  },
  {
    searchName: translate('slash.search.youtube'),
    category: 'Media',
    displayName: translate('slash.display.youtube'),
    icon: 'youtube',
    resource: [
      ResourceType.Page,
      ResourceType.Note,
      ResourceType.Card,
      ...ResourceVariants.Comment,
    ],
    filter: (store) => {
      return !selectionIsInTable(store);
    },
    run: (editor) => {
      showPopup({
        editorId: editor?.id,
        editor,
        youtubeVideoPopup: true,
        from: editor.state.selection.from,
        to: editor.state.selection.to,
      });
      stopSuggester(editor);
    },
  },
];

export const basicCommands: SlashCommand[] = [
  {
    searchName: translate('slash.search.checklist'),
    category: 'Basic',
    displayName: translate('slash.display.checklist'),
    icon: 'check-square',
    resource: [
      ResourceType.Page,
      ResourceType.Note,
      ResourceType.Card,
      ...ResourceVariants.Comment,
    ],
    run: (editor) => {
      editor
        .chain()
        .focus()
        .toggleTaskList()
        .setMeta(PhantomSuggesterKey.command as any, { type: 'remove' })
        .run();
    },
  },
  {
    searchName: translate('slash.search.table'),
    category: 'Basic',
    displayName: `${translate('slash.display.table')} (Beta)`,
    icon: 'table',
    resource: [ResourceType.Page, ResourceType.Note, ResourceType.Card],
    filter: (store) => {
      if (selectionIsInTable(store)) return false;
      return true;
    },
    run: (editor) => {
      editor
        .chain()
        .focus()
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .setMeta(PhantomSuggesterKey.command as any, { type: 'remove' })
        .run();
    },
  },
  {
    searchName: translate('slash.search.h1'),
    category: 'Basic',
    displayName: translate('slash.display.h1'),
    icon: 'H1',
    resource: [ResourceType.Page, ResourceType.Note, ResourceType.Card],
    run: (editor) => {
      editor
        .chain()
        .focus()
        .setHeading({ level: 1 })
        .setMeta(PhantomSuggesterKey.command as any, { type: 'remove' })
        .run();
    },
  },
  {
    searchName: translate('slash.search.h2'),
    category: 'Basic',
    displayName: translate('slash.display.h2'),
    icon: 'H2',
    resource: [ResourceType.Page, ResourceType.Note, ResourceType.Card],
    run: (editor) => {
      editor
        .chain()
        .focus()
        .setHeading({ level: 2 })
        .setMeta(PhantomSuggesterKey.command as any, { type: 'remove' })
        .run();
    },
  },
  {
    searchName: translate('slash.search.h3'),
    category: 'Basic',
    displayName: translate('slash.display.h3'),
    icon: 'H3',
    resource: [ResourceType.Page, ResourceType.Note, ResourceType.Card],
    run: (editor) => {
      editor
        .chain()
        .focus()
        .setHeading({ level: 3 })
        .setMeta(PhantomSuggesterKey.command as any, { type: 'remove' })
        .run();
    },
  },
  {
    searchName: translate('slash.search.blockquote'),
    category: 'Basic',
    displayName: translate('slash.display.blockquote'),
    icon: 'blocquote',
    resource: [
      ResourceType.Page,
      ResourceType.Note,
      ResourceType.Card,
      ...ResourceVariants.Comment,
    ],
    filter: (store) => {
      return !selectionIsInTable(store);
    },
    run: (editor) => {
      editor
        .chain()
        .focus()
        .toggleBlockquote()
        .setMeta(PhantomSuggesterKey.command as any, { type: 'remove' })
        .run();
    },
  },
  {
    searchName: translate('slash.search.bulleted'),
    category: 'Basic',
    displayName: translate('slash.display.bulleted'),
    icon: 'list',
    resource: [
      ResourceType.Page,
      ResourceType.Note,
      ResourceType.Card,
      ...ResourceVariants.Comment,
    ],
    run: (editor) => {
      editor
        .chain()
        .focus()
        .toggleBulletList()
        .setMeta(PhantomSuggesterKey.command as any, { type: 'remove' })
        .run();
    },
  },
  {
    searchName: translate('slash.search.numbered'),
    category: 'Basic',
    displayName: translate('slash.display.numbered'),
    icon: 'numbered-list',
    resource: [
      ResourceType.Page,
      ResourceType.Note,
      ResourceType.Card,
      ...ResourceVariants.Comment,
    ],
    run: (editor) => {
      editor
        .chain()
        .focus()
        .toggleOrderedList()
        .setMeta(PhantomSuggesterKey.command as any, { type: 'remove' })
        .run();
    },
  },
  {
    searchName: translate('slash.search.hr'),
    category: 'Basic',
    displayName: translate('slash.display.hr'),
    icon: 'minus',
    resource: [
      ResourceType.Page,
      ResourceType.Note,
      ResourceType.Card,
      ...ResourceVariants.Comment,
    ],
    filter: (store) => {
      return !selectionIsInTable(store);
    },
    run: (editor) => {
      editor
        .chain()
        .focus()
        .setHorizontalRule()
        .setMeta(PhantomSuggesterKey.command as any, { type: 'remove' })
        .run();
    },
  },
  {
    searchName: translate('slash.search.codeBlock'),
    category: 'Basic',
    displayName: translate('slash.display.codeBlock'),
    icon: 'code-block',
    resource: [
      ResourceType.Page,
      ResourceType.Note,
      ResourceType.Card,
      ...ResourceVariants.Comment,
    ],
    filter: (store) => {
      return !selectionIsInTable(store);
    },
    run: (editor) => {
      editor
        .chain()
        .focus()
        .toggleCodeBlock()
        .setMeta(PhantomSuggesterKey.command as any, { type: 'remove' })
        .run();
    },
  },
];

export const inlineCommands: SlashCommand[] = [
  {
    searchName: translate('slash.search.card'),
    category: 'Inline',
    displayName: translate('slash.display.card'),
    icon: 'new-card-icon',
    resource: [
      ResourceType.Page,
      ResourceType.Note,
      ResourceType.Card,
      ...ResourceVariants.Comment,
    ],
    filter: (store) => {
      return (
        !store.getters.getQuickCardIsOn &&
        !store.getters.getQuickPage &&
        !store.getters.getChildCardCreation
      );
    },
    run: (editor, _, store) => {
      const { view } = editor;
      const { from, to } = view.state.selection;

      editor.commands.setTextSelection(from);
      editor.commands.setTextSelection({ from, to });

      store.dispatch('setEditorForCardLink', editor?.id);
      store.dispatch('setQuickCard', true);

      stopSuggester(editor);
    },
  },
  {
    searchName: translate('slash.search.mention'),
    category: 'Inline',
    displayName: translate('slash.display.mention'),
    icon: 'at-sign',
    resource: [
      ResourceType.Page,
      ResourceType.Note,
      ResourceType.Card,
      ...ResourceVariants.Comment,
    ],
    run: (editor) => {
      editor
        .chain()
        .setMeta(PhantomSuggesterKey.command as any, { type: 'remove' })
        .setMeta(PhantomSuggesterKey.mention as any, {
          type: 'add',
          pos: editor.state.selection.anchor,
        })
        .run();
    },
  },

  {
    searchName: translate('slash.search.callout'),
    category: 'Basic',
    displayName: translate('slash.display.callout'),
    icon: 'info',
    resource: [
      ResourceType.Page,
      ResourceType.Note,
      ResourceType.Card,
      ...ResourceVariants.Comment,
    ],
    run: (editor) => {
      editor
        .chain()
        .focus()
        .callout({ type: CalloutTypes.Info })
        .setMeta(PhantomSuggesterKey.command as any, { type: 'remove' })
        .run();
    },
  },
];

export const embedCommands: SlashCommand[] = [
  {
    searchName: translate('slash.search.board'),
    category: 'Embed',
    displayName: translate('slash.display.board'),
    icon: 'board',
    resource: [ResourceType.Page],
    filter: (store) => {
      if (store.getters.currentEditor?.resourceId === ResourceType.PageTemplate) return false;
      return !store.getters.getQuickPage;
    },
    run: (editor, _, store) => {
      const myProjects = store.getters.getProjects.map((project: any) => project.id);
      const boards =
        Object.values(store.getters.getFlatBoards).filter(
          (board: any) => myProjects.includes(board.project_id) && !board.orginalId
        ) || [];
      store.dispatch('setSearchResults', { count: boards.length, cursor: '', boards });
      store.dispatch('setBoardEmbedPopup', {
        editorId: editor?.id,
      });
      stopSuggester(editor);
    },
  },
];

export const commands: SlashCommand[] = [
  ...mediaCommands,
  ...basicCommands,
  ...inlineCommands,
  ...embedCommands,
];

export function groupCommandsByType(
  commands: SlashCommand[],
  resourceType: ResourceType,
  store?: Store<any>
) {
  if (commands.length === 0) return [];

  const byResource = commands.filter((c) => c.resource.includes(resourceType));

  const filtered = store ? byResource.filter((c) => c.filter?.(store) ?? true) : byResource;

  const grouped = filtered.reduce<SearchResultCategory<SlashCommand>[]>((result, command) => {
    const category = result.find((r) => r.title === command.category);

    if (!category) {
      result.push({
        title: command.category,
        results: [command],
        previousIndex: 0,
        type: 'command',
      });
    } else {
      category.results.push(command);
    }
    return result;
  }, []);

  for (let i = 1; i < grouped.length; i++) {
    grouped[i].previousIndex = grouped[i - 1].previousIndex + grouped[i - 1].results.length;
  }

  return grouped;
}

export const list = new Fuse(commands, {
  includeScore: false,
  keys: ['searchName'],
});
